import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PencilLine, ChatsCircle, BookOpenText, Buildings, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export const AppHead = ({ title, sub }) => {
  const { t } = useT();
  return (
  <div className="mb-8">
    <h1 className="text-3xl sm:text-4xl text-[#0F4C5C]">{t(title)}</h1>
    {sub && <p className="mt-2 text-gray-600">{t(sub)}</p>}
  </div>
  );
};

export const usePropertyList = () => {
  const [props, setProps] = useState([]);
  const load = () => api.get("/properties").then((r) => setProps(r.data));
  useEffect(() => { load(); }, []);
  return [props, load];
};

export default function Dashboard() {
  const { t } = useT();
  const { user } = useAuth();
  const [props] = usePropertyList();
  const [gens, setGens] = useState([]);
  useEffect(() => { api.get("/generations").then((r) => setGens(r.data.slice(0, 5))); }, []);
  const tiles = [
    ["/app/rewriter", "Rewrite a listing", PencilLine, "Claude-written title, description and highlights."],
    ["/app/messages", "Write a guest message", ChatsCircle, "Confirmation to review request, 10 languages."],
    ["/app/guidebooks", "Build a guidebook", BookOpenText, "Shareable link and printable PDF."],
  ];
  return (
    <div data-testid="app-dashboard">
      <AppHead title={t("Good to see you, {name}.", { name: user.name?.split(" ")[0] || t("host") })} sub="What are we writing today?" />
      <div className="grid md:grid-cols-3 gap-4">
        {tiles.map(([to, title, I, d]) => (
          <Link key={to} to={to} className="card card-hover p-6 bg-white group" data-testid={`dash-tile-${to.split("/").pop()}`}>
            <I size={28} weight="duotone" className="text-[#E36414]" />
            <p className="font-display text-xl text-[#0F4C5C] mt-3 flex items-center gap-2">{t(title)} <ArrowRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity" /></p>
            <p className="text-sm text-gray-600 mt-1">{t(d)}</p>
          </Link>
        ))}
      </div>
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-4 mt-4">
        <div className="card p-6 bg-white" data-testid="dash-properties-card">
          <div className="flex justify-between items-center"><p className="label !mb-0">{t("Properties")}</p><Link to="/app/properties" className="text-xs text-[#E36414] font-semibold">{t("Manage")}</Link></div>
          <p className="font-display text-4xl text-[#0F4C5C] mt-3">{props.length}<span className="text-base text-gray-500"> / {user.property_limit}</span></p>
          <ul className="mt-3 space-y-1 text-sm">{props.slice(0, 4).map((p) => <li key={p.property_id} className="flex items-center gap-2"><Buildings size={14} /> {p.name} <span className="text-gray-500">{p.city}</span></li>)}</ul>
        </div>
        <div className="card p-6 bg-white" data-testid="dash-recent-card">
          <p className="label">{t("Recent work")}</p>
          {gens.length === 0 ? <p className="text-sm text-gray-500">{t("Nothing yet. Your rewrites and messages will appear here.")}</p> : (
            <ul className="divide-y divide-[#E2DCD0]">
              {gens.map((g) => (
                <li key={g.gen_id} className="py-2.5 text-sm flex justify-between gap-4">
                  <span className="truncate"><span className="uppercase text-[10px] font-semibold tracking-wider bg-[#F4E9D8] text-[#0F4C5C] px-2 py-0.5 rounded mr-2">{t(g.kind)}</span>
                    {g.kind === "rewrite" ? g.output?.title : g.kind === "outreach" ? `${g.input?.host_name || t("Host")} · ${g.output?.score}/100` : t(g.input?.message_type)}</span>
                  <span className="text-gray-500 shrink-0">{g.created_at.slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
